import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { usePermission } from '../context/PermissionContext';
import { User, Plus, X, Loader2, ArrowLeft, Building2 } from 'lucide-react';

const ResponsiblePersonAssignment = ({ entityType, entityLabel, baseApiPath }) => {
  const { id } = useParams();
  const entityId = Number(id);
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canAssign = hasPermission(entityType, 'assign');
  const apiPrefix = baseApiPath || `/api/${entityType}s`;

  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assignedList, setAssignedList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state for adding a new responsible person
  const [selectedPositionId, setSelectedPositionId] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [adding, setAdding] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rpRes, empRes, posRes] = await Promise.all([
        fetch(`${apiPrefix}/${entityId}/responsible-persons`),
        fetch(`${apiPrefix}/${entityId}/assignable-employees`),
        fetch('/api/organization/positions'),
      ]);

      const rpData = await rpRes.json();
      if (rpData.status === 'success') {
        setAssignedList(rpData.data || []);
      }

      const empData = await empRes.json();
      if (empData.status === 'success') {
        setEmployees(empData.data || []);
      }

      const posData = await posRes.json();
      if (posData.status === 'success') {
        const list = posData.data?.data || posData.data || [];
        setPositions(list);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(t('common.load_data_failed'));
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh assigned list without full reload
  const refreshAssigned = async () => {
    try {
      const res = await fetch(`${apiPrefix}/${entityId}/responsible-persons`);
      const data = await res.json();
      if (data.status === 'success') {
        setAssignedList(data.data || []);
      }
    } catch (err) {
      console.error('Refresh failed:', err);
    }
  };

  const handleAdd = async () => {
    if (!selectedPositionId || !selectedEmployeeId) return;
    setAdding(true);
    try {
      const res = await fetch(`${apiPrefix}/${entityId}/responsible-persons/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          position_id: Number(selectedPositionId),
          employee_id: Number(selectedEmployeeId),
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setSelectedPositionId('');
        setSelectedEmployeeId('');
        await refreshAssigned();
      } else {
        setError(data.message || t('common.add_failed'));
      }
    } catch (err) {
      setError(t('common.network_error'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (rpId) => {
    try {
      const res = await fetch(`${apiPrefix}/${entityId}/responsible-persons/remove`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: rpId }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setAssignedList(prev => prev.filter(p => p.id !== rpId));
      }
    } catch (err) {
      console.error('Remove failed:', err);
    }
  };

  // Get employee display name by ID
  const getEmployeeDisplay = (empId) => {
    const emp = employees.find(e => Number(e.entity_id) === Number(empId));
    if (!emp) return t('common.employee_prefix', { id: empId });
    const name = [(emp.first_name || ''), (emp.last_name || '')].filter(Boolean).join('');
    return name ? `${emp.entity_id} - ${name}` : emp.email;
  };

  // Get position name by ID
  const getPositionName = (posId) => {
    const pos = positions.find(p => Number(p.id) === Number(posId));
    return pos ? pos.name : t('profile.position') + ` #${posId}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-blue-500 mr-2" />
        <span className="text-slate-400">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => window.history.back()}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <ArrowLeft size={20} className="text-slate-500" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">
            {t('common.assign_responsible')}
          </h2>
          <p className="text-sm text-slate-500">
            {entityLabel} (ID: {entityId})
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
          {error}
          <button onClick={() => setError('')} className="float-right">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="space-y-6">
        {/* Add new responsible person */}
        {canAssign && (
          <div className="bg-white dark:bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
              <Plus size={16} className="text-blue-500" />
              {t('common.add_responsible')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              {/* Position selector */}
              <div className="space-y-1">
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  <Building2 size={12} /> {t('common.responsible_position_label')}
                </label>
                <select
                  className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent dark:text-white"
                  value={selectedPositionId}
                  onChange={(e) => setSelectedPositionId(e.target.value)}
                >
                  <option value="">{t('common.select_position_tag_placeholder')}</option>
                  {positions.map(p => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Employee selector */}
              <div className="space-y-1">
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  <User size={12} /> {t('common.select_employee_label')}
                </label>
                <select
                  className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent dark:text-white"
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                >
                  <option value="">{t('common.select_employee_tag_placeholder')}</option>
                  {employees.map(e => (
                    <option key={e.entity_id} value={String(e.entity_id)}>
                      {e.entity_id} - {(e.first_name || '')}{(e.last_name || '')}
                    </option>
                  ))}
                </select>
              </div>

              {/* Add button */}
              <button
                onClick={handleAdd}
                disabled={!selectedPositionId || !selectedEmployeeId || adding}
                className="py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {t('common.add')}
              </button>
            </div>
          </div>
        )}

        {/* Currently assigned */}
        <div className="bg-white dark:bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
          <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
            <User size={16} className="text-green-500" />
            {t('common.current_responsible_count', { count: assignedList.length })}
          </h3>

          {assignedList.length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-400 italic">
              {t('common.no_responsible')}
            </div>
          ) : (
            <div className="space-y-2">
              {assignedList.map(p => (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                      {(p.email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {getEmployeeDisplay(p.employee_id)}
                      </div>
                      <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                        <Building2 size={10} />
                        {p.position_name || getPositionName(p.position_id)}
                      </div>
                    </div>
                  </div>
                  {canAssign && (
                    <button
                      onClick={() => handleRemove(p.id)}
                      className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      title={t('common.remove')}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResponsiblePersonAssignment;
